import React, { useState, useCallback } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip
} from 'recharts';
import { Download, FileText, BarChart3, ChevronLeft, ChevronRight } from 'lucide-react';
import type { SurveyAnalyticsReport as SurveyAnalyticsReportType, QuestionAnalytics } from '../../services/reporting';
import { surveyReportsService } from '../../services/reporting/surveyReports.service';
import { surveyService } from '../../services/survey.service';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { toast } from 'react-hot-toast';

interface AvailableSurvey {
  id: string;
  name: string;
}

interface SurveyAnalyticsReportProps {
  surveyAnalytics: SurveyAnalyticsReportType | null;
  availableSurveys: AvailableSurvey[];
  selectedSurveyId: string;
  onSurveyChange: (surveyId: string) => void;
  loading: boolean;
}

// A single response displayed as a readable card.
interface ResponseCard {
  id: string;
  residentName: string;
  phone: string;
  email: string;
  address: string;
  canvasser: string;
  date: string;
  status: string;
  answers: Array<{ question: string; answer: string }>;
}

const SurveyAnalyticsReport: React.FC<SurveyAnalyticsReportProps> = ({
  surveyAnalytics,
  availableSurveys,
  selectedSurveyId,
  onSurveyChange,
  loading
}) => {
  const [viewMode, setViewMode] = useState<'analytics' | 'responses'>('analytics');
  const [responseCards, setResponseCards] = useState<ResponseCard[]>([]);
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [currentResponseIndex, setCurrentResponseIndex] = useState(0);
  const [exportingCsv, setExportingCsv] = useState(false);

  // Load individual response cards for the "Response Detail" view.
  const loadResponseCards = useCallback(async () => {
    if (!selectedSurveyId) return;
    setLoadingResponses(true);
    try {
      const survey = await surveyService.get(selectedSurveyId);
      if (!survey) {
        toast.error('Survey not found');
        setLoadingResponses(false);
        return;
      }

      // Simple query by surveyId only — no compound index required.
      let responses: any[] = [];
      try {
        const snap = await getDocs(
          query(collection(db, 'surveyResponses'), where('surveyId', '==', selectedSurveyId))
        );
        responses = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      } catch (queryErr: any) {
        console.error('Firestore query failed:', queryErr);
        // If the direct query fails (index issue), try via the reports service
        // which is known to work for the analytics view.
        const analytics = await surveyReportsService.generateSurveyAnalytics(selectedSurveyId);
        // Fall back to an empty list with a user-facing message
        toast.error(`Query error: ${queryErr.message?.slice(0, 100)}`);
        setLoadingResponses(false);
        return;
      }

      const cards: ResponseCard[] = responses.map((r: any) => ({
        id: r.id,
        residentName: r.residentName || 'Unknown',
        phone: r.residentPhone || '',
        email: r.residentEmail || '',
        address: r.addressStreet || '',
        canvasser: r.submittedByName || r.submittedBy || 'Unknown',
        date: r.completedAt?.toDate?.()?.toLocaleDateString?.() || r.startedAt?.toDate?.()?.toLocaleDateString?.() || '',
        status: r.discontinued ? 'Discontinued' : 'Completed',
        answers: survey.questions.map((q) => {
          const raw = r.responses?.[q.id];
          // Resolve option IDs to human-readable text for choice questions.
          const resolve = (v: string): string => {
            if (!q.options || q.options.length === 0) return v;
            const match = q.options.find((o: any) => o.id === v || o.value === v);
            return match ? match.text : v;
          };
          let answer = '';
          if (raw == null) {
            answer = '';
          } else if (Array.isArray(raw)) {
            answer = raw.map((v) => resolve(String(v ?? ''))).filter(Boolean).join(', ');
          } else {
            const s = String(raw);
            answer = s === 'undefined' ? '' : resolve(s);
          }
          return { question: q.text, answer };
        }),
      }));

      setResponseCards(cards);
      setCurrentResponseIndex(0);
    } catch (err) {
      console.error('Failed to load response cards:', err);
      toast.error('Failed to load responses');
    } finally {
      setLoadingResponses(false);
    }
  }, [selectedSurveyId]);

  const handleExportCSV = useCallback(async () => {
    if (!selectedSurveyId) return;
    setExportingCsv(true);
    try {
      const csv = await surveyReportsService.exportResponsesToCSV(selectedSurveyId);
      if (!csv) {
        toast.error('No responses to export');
        return;
      }
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const surveyName = availableSurveys.find(s => s.id === selectedSurveyId)?.name || 'survey';
      link.setAttribute('href', url);
      link.setAttribute('download', `${surveyName}-responses.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (err) {
      console.error('CSV export failed:', err);
      toast.error('Failed to export CSV');
    } finally {
      setExportingCsv(false);
    }
  }, [selectedSurveyId, availableSurveys]);

  const currentCard = responseCards[currentResponseIndex];
  return (
    <div className="space-y-6">
      {/* Survey Selector */}
      {availableSurveys.length > 0 && (
        <div className="bg-white p-4 rounded-lg shadow">
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Survey</label>
          <select
            value={selectedSurveyId}
            onChange={(e) => onSurveyChange(e.target.value)}
            className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md"
          >
            {availableSurveys.map((survey) => (
              <option key={survey.id} value={survey.id}>{survey.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* View toggle + Export CSV */}
      {selectedSurveyId && (
        <div className="bg-white p-4 rounded-lg shadow flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center border border-gray-300 rounded-md">
            <button
              onClick={() => setViewMode('analytics')}
              className={`flex items-center gap-1 px-3 py-2 text-sm rounded-l-md ${viewMode === 'analytics' ? 'bg-indigo-50 text-indigo-600 font-medium' : 'text-gray-500 hover:bg-gray-50'}`}
            >
              <BarChart3 className="h-4 w-4" />
              Analytics
            </button>
            <button
              onClick={() => {
                setViewMode('responses');
                if (responseCards.length === 0) loadResponseCards();
              }}
              className={`flex items-center gap-1 px-3 py-2 text-sm rounded-r-md ${viewMode === 'responses' ? 'bg-indigo-50 text-indigo-600 font-medium' : 'text-gray-500 hover:bg-gray-50'}`}
            >
              <FileText className="h-4 w-4" />
              Response Detail
            </button>
          </div>
          <button
            onClick={handleExportCSV}
            disabled={exportingCsv}
            className="flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            {exportingCsv ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      )}

      {/* Response Detail View — one card per response */}
      {viewMode === 'responses' && (
        <div>
          {loadingResponses ? (
            <div className="text-center py-12 text-gray-500">Loading responses…</div>
          ) : responseCards.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No responses found.</div>
          ) : (
            <div className="space-y-4">
              {/* Navigation */}
              <div className="bg-white rounded-lg shadow p-4 flex items-center justify-between">
                <button
                  onClick={() => setCurrentResponseIndex(Math.max(0, currentResponseIndex - 1))}
                  disabled={currentResponseIndex === 0}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-30"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </button>
                <span className="text-sm text-gray-600 font-medium">
                  Response {currentResponseIndex + 1} of {responseCards.length}
                </span>
                <button
                  onClick={() => setCurrentResponseIndex(Math.min(responseCards.length - 1, currentResponseIndex + 1))}
                  disabled={currentResponseIndex >= responseCards.length - 1}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-30"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              {/* Card */}
              {currentCard && (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  {/* Resident header */}
                  <div className="bg-indigo-50 p-4 border-b border-indigo-100">
                    <h3 className="text-lg font-semibold text-gray-900">{currentCard.residentName}</h3>
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
                      {currentCard.phone && <span>Phone: {currentCard.phone}</span>}
                      {currentCard.email && <span>Email: {currentCard.email}</span>}
                      {currentCard.address && <span>Address: {currentCard.address}</span>}
                    </div>
                    <div className="flex flex-wrap gap-4 mt-1 text-xs text-gray-500">
                      <span>Canvasser: {currentCard.canvasser}</span>
                      <span>Date: {currentCard.date}</span>
                      <span className={currentCard.status === 'Completed' ? 'text-green-600' : 'text-orange-600'}>
                        {currentCard.status}
                      </span>
                    </div>
                  </div>

                  {/* Answers */}
                  <div className="divide-y divide-gray-100">
                    {currentCard.answers.map((qa, i) => (
                      <div key={i} className="p-4">
                        <p className="text-sm font-medium text-gray-700">
                          <span className="text-indigo-600 mr-1">Q{i + 1}.</span>
                          {qa.question}
                        </p>
                        <p className="mt-1 text-sm text-gray-900">
                          {qa.answer || <span className="text-gray-400 italic">No answer</span>}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {viewMode === 'analytics' && surveyAnalytics && (
        <>
          {/* Response Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-sm text-gray-500">Total Responses</p>
              <p className="text-2xl font-bold text-gray-900">{surveyAnalytics.responseMetrics.totalResponses}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-sm text-gray-500">Completed</p>
              <p className="text-2xl font-bold text-green-600">{surveyAnalytics.responseMetrics.completedResponses}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-sm text-gray-500">Discontinued</p>
              <p className="text-2xl font-bold text-orange-600">{surveyAnalytics.responseMetrics.discontinuedResponses}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-sm text-gray-500">Avg Completion Time</p>
              <p className="text-2xl font-bold text-indigo-600">{surveyAnalytics.responseMetrics.avgCompletionTime} min</p>
            </div>
          </div>

          {/* Response Trend */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-medium mb-4">Response Trend</h3>
            {surveyAnalytics.responseMetrics.responsesByDay && surveyAnalytics.responseMetrics.responsesByDay.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={surveyAnalytics.responseMetrics.responsesByDay}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={(val) => val?.slice?.(5) || val} />
                    <YAxis />
                    <Tooltip />
                    <Area type="monotone" dataKey="count" name="Responses" stroke="#6366F1" fill="#6366F1" fillOpacity={0.3} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500">
                No response trend data available for this time range.
              </div>
            )}
          </div>

          {/* Question Analytics */}
          {surveyAnalytics.questionAnalytics.map((q: QuestionAnalytics, qi: number) => (
            <div
              key={qi}
              className="bg-white p-6 rounded-lg shadow mb-6 question-block"
              style={{
                pageBreakBefore: qi > 0 ? 'always' : 'auto',
                breakBefore: qi > 0 ? 'page' : 'auto',
                pageBreakInside: 'avoid',
                breakInside: 'avoid'
              }}
            >
              {/* Question header */}
              <div className="mb-4 bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-500">
                <div className="flex items-start gap-3">
                  <span className="text-2xl font-bold text-indigo-600">Q{qi + 1}</span>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-lg">{q.questionText || 'Unknown Question'}</h4>
                    <span className="text-sm text-gray-600">
                      {q.totalResponses ?? 0} responses ({q.responseRate ?? 0}% response rate)
                    </span>
                  </div>
                </div>
              </div>

              {/* Choice distribution for choice questions */}
              {q.choiceDistribution && q.choiceDistribution.length > 0 && (
                <div className="space-y-3">
                  {q.choiceDistribution.map((choice, ci) => (
                    <div key={ci} className="space-y-1">
                      <div className="flex justify-between items-baseline">
                        <span className="text-sm text-gray-600 break-words max-w-[70%]">{choice.value || 'N/A'}</span>
                        <span className="text-sm text-gray-500 whitespace-nowrap ml-2">{choice.count ?? 0} ({choice.percentage ?? 0}%)</span>
                      </div>
                      <div className="h-4 bg-gray-200 rounded w-full">
                        <div
                          className="h-4 bg-indigo-500 rounded"
                          style={{ width: `${Math.min(choice.percentage ?? 0, 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Rating stats for rating questions */}
              {q.ratingStats && (
                <div className="mt-2 text-sm">
                  <span className="text-gray-600">Avg Rating: <strong>{q.ratingStats.avgRating ?? 0}</strong></span>
                </div>
              )}

              {/* Text stats for text questions */}
              {q.textStats && (
                <div className="mt-2">
                  <span className="text-sm text-gray-600">Avg Length: {q.textStats.avgLength ?? 0} chars</span>
                  {q.textStats.wordCloud && q.textStats.wordCloud.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {q.textStats.wordCloud
                        .filter(word => word.word && word.word !== 'undefined')
                        .slice(0, 10)
                        .map((word, wi) => (
                          <span
                            key={wi}
                            className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-sm"
                            style={{ fontSize: `${Math.min(0.75 + (word.count ?? 0) * 0.05, 1.25)}rem` }}
                          >
                            {word.word} ({word.count ?? 0})
                          </span>
                        ))}
                    </div>
                  )}
                </div>
              )}

              {/* Numeric stats */}
              {q.numericStats && (
                <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-gray-600">
                  <span>Min: {q.numericStats.min ?? 0}</span>
                  <span>Max: {q.numericStats.max ?? 0}</span>
                  <span>Avg: {q.numericStats.avg ?? 0}</span>
                  <span>Median: {q.numericStats.median ?? 0}</span>
                </div>
              )}

              {/* Show all actual responses - text/open-ended questions */}
              {q.allResponses && q.allResponses.length > 0 && !q.choiceDistribution && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">All Responses ({q.allResponses.length}):</p>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-sm">
                    {q.allResponses.slice(0, 15).map((response, ri) => {
                      const displayValue = Array.isArray(response.value)
                        ? response.value.filter(v => v != null && v !== 'undefined').join(', ')
                        : (response.value != null && response.value !== 'undefined' ? String(response.value) : '');
                      return (
                        <div key={ri} className="py-1 border-b border-gray-200 last:border-0">
                          <span className="text-gray-500">{ri + 1}.</span>{' '}
                          <span className="text-gray-800">{displayValue || 'No response'}</span>
                        </div>
                      );
                    })}
                    {q.allResponses.length > 15 && (
                      <div className="text-gray-500 italic pt-2">
                        ... and {q.allResponses.length - 15} more responses
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* For choice questions, show summary of individual responses */}
              {q.allResponses && q.allResponses.length > 0 && q.choiceDistribution && q.choiceDistribution.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Individual Responses ({q.allResponses.length}):</p>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-sm">
                    {q.allResponses.slice(0, 10).map((response, ri) => {
                      const displayValue = Array.isArray(response.value)
                        ? response.value.filter(v => v != null && v !== 'undefined').join(', ')
                        : (response.value != null && response.value !== 'undefined' ? String(response.value) : '');
                      return (
                        <div key={ri} className="py-1 border-b border-gray-200 last:border-0">
                          <span className="text-gray-500">{ri + 1}.</span>{' '}
                          <span className="text-gray-800">{displayValue || 'No response'}</span>
                        </div>
                      );
                    })}
                    {q.allResponses.length > 10 && (
                      <div className="text-gray-500 italic pt-2">
                        ... and {q.allResponses.length - 10} more responses
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Canvasser Distribution */}
          {surveyAnalytics.canvasserDistribution && surveyAnalytics.canvasserDistribution.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-medium mb-4">Responses by Canvasser</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Canvasser</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Responses</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Completion Time</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {surveyAnalytics.canvasserDistribution
                      .filter(c => c.userName && c.userName !== 'undefined' && c.userName !== 'Unknown')
                      .map((c, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{c.userName || 'Anonymous'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{c.responseCount ?? 0}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{c.avgCompletionTime ?? 0} min</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {loading && (
        <div className="text-center text-gray-500 py-12">
          <p>Loading survey analytics...</p>
        </div>
      )}

      {!surveyAnalytics && !loading && availableSurveys.length === 0 && (
        <div className="text-center text-gray-500 py-12">
          <p>No surveys found for this project.</p>
        </div>
      )}

      {!surveyAnalytics && !loading && availableSurveys.length > 0 && (
        <div className="text-center text-gray-500 py-12">
          <p>Select a survey above to view analytics.</p>
        </div>
      )}
    </div>
  );
};

export default SurveyAnalyticsReport;
